/**
 * Market-aware outcome derivation.
 *
 * Given a prediction market and the current value string, returns the
 * outcome key that can be matched against odds selections.
 *
 * For CORRECT_SCORE the outcome is *derived* from the score pair.
 * For selection markets the value *is* the outcome.
 */

import type { PredictionMarket } from "./markets";

/**
 * Derive the outcome key from a prediction value.
 *
 * @returns The outcome key (e.g. "1", "X", "2", "yes", "no", "over", "under")
 *          or `null` if the value is empty / unparseable.
 *
 * @example
 *   deriveOutcomeKey("CORRECT_SCORE", "2:1") // "1"
 *   deriveOutcomeKey("CORRECT_SCORE", "1:1") // "X"
 *   deriveOutcomeKey("FT_1X2", "2")          // "2"
 *   deriveOutcomeKey("BOTH_TEAMS_SCORE", "yes") // "yes"
 */
export function deriveOutcomeKey(market: PredictionMarket, value: string): string | null {
    if (!value) return null;

    switch (market) {
        case "CORRECT_SCORE":
        case "CORRECT_SCORE_ADVANCED":
        case "CORRECT_SCORE_HT": {
            const parts = value.split(":");
            if (parts.length !== 2) return null;
            const h = parseInt(parts[0], 10);
            const a = parseInt(parts[1], 10);
            if (isNaN(h) || isNaN(a)) return null;
            if (h > a) return "1";
            if (h < a) return "2";
            return "X";
        }

        case "FT_1X2":
        case "DOUBLE_CHANCE":
        case "BOTH_TEAMS_SCORE":
        case "OVER_UNDER":
            return value;

        default:
            return null;
    }
}

/**
 * Encode a score pair into the SDK value format.
 * @example scoreToValue(2, 1) // "2:1"
 */
export function scoreToValue(home: number, away: number): string {
    return `${home}:${away}`;
}

/**
 * Decode an SDK value string into a score pair.
 * Returns [0, 0] if the value is not a valid score.
 */
export function valueToScores(value: string): [number, number] {
    const parts = value.split(":");
    if (parts.length !== 2) return [0, 0];
    const h = parseInt(parts[0], 10);
    const a = parseInt(parts[1], 10);
    return [isNaN(h) ? 0 : h, isNaN(a) ? 0 : a];
}
